import { navigate } from 'gatsby';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-use';
import styled from 'styled-components';

import { media } from 'atoms/breakpoints/breakpoints';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Text from 'quarks/Text';

import Section from 'molecules/Section/Section';

import ComponentButton from 'components/Button/ComponentButton';
import useResourceCardDeckPosts from 'components/CardDeck/utils/useResourceCardDeckPosts';
import ResourceCard from 'components/Cards/ResourceCard';
import Body from 'components/ResourceDetailLayout/components/Body';
import Sidebar from 'components/ResourceDetailLayout/components/Sidebar/Sidebar';
import TemplateHubFilter from 'components/TemplateHub/TemplateHubFilter';

type TemplateHubProps = {
  backgroundIsDark?: boolean;
  stage?: string;
};

type PostCountProps = {
  className?: string;
};

const TemplateHub: FC<TemplateHubProps> = ({ backgroundIsDark, stage }) => {
  const [numOfPosts, setNumOfPosts] = useState(4);
  const [allTemplates, setAllTemplates] = useState(null);
  const [postsToShow, setPostToShow] = useState([]);
  const [filterValues, setFilterValues] = useState({
    industry: null,
    stage: null,
  });
  const [tempFilterValues, setTempFilterValues] = useState({
    industry: null,
    stage: null,
  });
  const [filterDropdownData, setFilterDropdownData] = useState({
    industry: [],
    stage: [],
  });

  const { pathname } = useLocation();

  const { landingPageTemplate, templates } = useResourceCardDeckPosts();

  const { body, tableOfContents } = landingPageTemplate?.nodes?.[0];

  useEffect(() => {
    const pathIndustry = pathname
      .slice(window.location.pathname.lastIndexOf('/') + 1)
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const values = { industry: pathIndustry, stage };

    if (pathIndustry !== 'Templates') {
      setFilterValues(values);
      setTempFilterValues(values);
    }
  }, [pathname]);

  useEffect(() => {
    if (!allTemplates) {
      return;
    }

    const { industry, stage: filterStage } = filterValues;
    const hasFilterValues = industry || filterStage;

    const filteredPosts = allTemplates.filter(template => {
      const matchesIndustry = !industry || template?.industry?.includes(industry);
      const matchesStage = !filterStage || template?.jobStage?.includes(filterStage);

      return filterStage === 'All Stages of the Job'
        ? matchesIndustry
        : hasFilterValues
        ? matchesIndustry && matchesStage
        : matchesIndustry || matchesStage;
    });

    const basePath = '/resources/templates/';
    const forwardedPath =
      industry === 'All Industries' || !industry ? basePath : `${basePath}${industry?.replace(' ', '-').toLowerCase()}`;

    navigate(forwardedPath, { state: { stage: filterStage } });

    setPostToShow(hasFilterValues ? filteredPosts : allTemplates);
  }, [filterValues, allTemplates]);

  useEffect(() => {
    const templateNodes = templates?.nodes?.map(node => node);

    setAllTemplates(templateNodes);
    getFilterData(templateNodes);
  }, [templates]);

  const getFilterData = templateNodes => {
    const dropdownData = templateNodes.reduce(
      (dropdownList, template) => {
        const { industry, jobStage } = template;

        ['industry', 'jobStage'].forEach(type => {
          const value = type === 'industry' ? industry : jobStage;

          if (!dropdownList[type].some(obj => obj.value === value)) {
            dropdownList[type].push({ label: value, value });
          }
        });

        return dropdownList;
      },
      { industry: [], jobStage: [] },
    );

    const addAllIndustries = { label: 'All Industries', value: 'All Industries' };
    const addAllStages = { label: 'All Stages of the Job', value: 'All Stages of the Job' };

    dropdownData.industry.push(addAllIndustries);
    dropdownData.jobStage.push(addAllStages);

    ['industry', 'jobStage'].forEach(type => {
      dropdownData[type] = dropdownData[type]
        .filter(option => option.label !== null && option.value !== null)
        .sort((a, b) => a.label.localeCompare(b.label));
    });

    setFilterDropdownData(dropdownData);
  };

  const PostCount: FC<PostCountProps> = ({ className }) => (
    <Text as="p" fontWeight="medium" textColor="gray-900" className={className}>
      Showing {postsToShow.length ? '1' : '0'}-{numOfPosts > postsToShow.length ? postsToShow.length : numOfPosts} of{' '}
      {postsToShow.length}
    </Text>
  );

  return (
    <Container>
      <Flex className="inner-container">
        <Flex className="filter">
          <TemplateHubFilter
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            setTempFilterValues={setTempFilterValues}
            tempFilterValues={tempFilterValues}
            dropdownData={filterDropdownData}
            PostCount={PostCount}
          />
        </Flex>
        <Flex className="cards">
          <Flex className="cards-heading">
            <Heading as="h3" fontFamily="secondaryFont" textColor="gray-900" marginBottom={20}>
              {filterValues.stage || 'All Stages of the Job'}
            </Heading>
            <PostCount className="count-desktop" />
          </Flex>
          <div className="cards-container">
            <Flex className="cards-wrapper">
              {postsToShow.length > 0 ? (
                postsToShow
                  ?.slice(0, numOfPosts)
                  ?.map((post, index) => (
                    <ResourceCard pageType="templates" backgroundIsDark={backgroundIsDark} {...post} key={index} />
                  ))
              ) : (
                <Text textStyle="textMd" textColor="gray-700">
                  No templates were found that match the search criteria
                </Text>
              )}
            </Flex>
            {numOfPosts < postsToShow.length && (
              <ComponentButton
                label="See More"
                buttonColor="Sunrise 500"
                isButton
                handleClick={() => setNumOfPosts(numOfPosts + 4)}
              />
            )}
          </div>
        </Flex>
      </Flex>
      <div className="rich-text">
        {body && (
          <Section
            paddingTopDesktop="72"
            paddingBottomDesktop="48"
            paddingTopTablet="64"
            paddingBottomTablet="64"
            paddingTopMobile="48"
            paddingBottomMobile="48"
          >
            <Flex
              flexDirection="column-reverse"
              justifyContent="space-between"
              width="100%"
              gap="32px"
              lg={{ flexDirection: 'row' }}
            >
              <Body body={body} />
              {tableOfContents && <Sidebar tocProps={{ tableOfContents }} showGetDemo />}
            </Flex>
          </Section>
        )}
      </div>
    </Container>
  );
};

export default TemplateHub;

const Container = styled.div`
  .filter {
    flex: 100%;
    height: 350px;
  }

  .cards-container > button {
    margin-top: 40px;
  }

  .count-desktop {
    display: none;
  }

  .count-mobile {
    display: flex;
  }

  .inner-container {
    gap: 40px;
    flex-direction: column;
  }

  .cards {
    flex: 100%;
    flex-direction: column;
    padding-bottom: 50px;
  }

  .cards-heading {
    margin-bottom: 30px;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
    }

    p {
      font-size: 18px;
    }
  }

  .rich-text .sidebar-demo a {
    margin: 0;
  }

  .cards-wrapper {
    flex-wrap: wrap;
    gap: 40px;

    a {
      flex: 100%;
    }
  }

  button {
    margin: 0 auto 0 auto;
  }

  ${media.sm} {
    .cards-wrapper > a {
      max-width: calc(50% - 20px);
    }
  }

  ${media.lg} {
    .inner-container {
      flex-direction: row;
    }

    .filter {
      flex: 29%;
      position: sticky;
      top: 130px;
    }

    .cards {
      flex: 71%;
    }

    .count-desktop {
      display: flex;
    }

    .count-mobile {
      display: none;
    }
  }
`;
